import { useContext, useEffect, useState } from "react"
import { DataRowProps, TreeProps } from "./Tree"
import TreeCreateEntryButton from "./TreeCreateEntryButton"
import { TreeContext } from "./TreeProvider"
import TreeLabel from "./TreeLabel"

const TREE_BUTTON_CLASS = "purple pe-3 clickable"
const COLLAPSED_CLASS = `bi bi-caret-right-fill ${TREE_BUTTON_CLASS}`
const EXPANDED_CLASS = `bi bi-caret-down-fill ${TREE_BUTTON_CLASS}`
const LEAF_CLASS = "bi bi-circle-fill purple pe-3"

interface TreeNodeProps {
  id: number
  treeData: DataRowProps[]
  selectedId?: number
  singleLevel?: number
  setProps: (props: Partial<TreeProps>) => void
}

const TreeNode = ({ id, treeData, selectedId, singleLevel, setProps }: TreeNodeProps) => {
  // For each child, pop its entry and pass it to a Tree Node
  const {
    label,
    level,
    can_create,
    can_delete,
    can_edit,
    can_copy,
    can_move,
    can_select,
    ...extra_fields
  } = treeData.find((entry) => entry.id === id) as DataRowProps

  const index = treeData.findIndex((entry) => entry.id === id)
  const treeDataChildren =
    singleLevel == undefined ? treeData.filter((entry) => entry.parent_id === id) : []

  const { expandedIds, levelClassName, enableEdit, enableCopy, enableDelete } =
    useContext(TreeContext)

  const [expanded, setExpanded] = useState(expandedIds.includes(id))

  const toggleExpand = () => {
    setExpanded((is_expanded) => !is_expanded)
  }

  useEffect(() => {
    if (expandedIds.includes(id)) {
      setExpanded(true)
    }
  }, [expandedIds, id])

  let icon_class = LEAF_CLASS
  let icon_style: { [cssProp: string]: number | string } = { fontSize: "7px" }
  const is_parent = treeDataChildren.length > 0
  if (is_parent || can_create) {
    icon_class = expanded ? EXPANDED_CLASS : COLLAPSED_CLASS
    icon_style = {}
  }
  const className = singleLevel == undefined ? levelClassName[level] ?? "" : ""
  return (
    <div className="ms-4 my-2">
      <div className="a-middle">
        <i className={icon_class} onClick={toggleExpand} style={icon_style} />
        <TreeLabel
          index={index}
          id={id}
          selectedId={selectedId}
          canDelete={enableDelete == undefined ? can_delete : enableDelete}
          canEdit={enableEdit == undefined ? can_edit : enableEdit}
          canCopy={enableCopy == undefined ? can_copy : enableCopy}
          canMove={can_move}
          canSelect={can_select}
          setProps={setProps}
          setExpandParent={setExpanded}
          className={className}
          extraFields={extra_fields}
        >
          {label}
        </TreeLabel>
      </div>
      {(is_parent || can_create) && (
        <div hidden={!expanded} className="border-start ms-2">
          {can_create && <TreeCreateEntryButton index={index} id={id} setProps={setProps} />}
          {is_parent &&
            treeDataChildren.map((child) => (
              <TreeNode
                id={child.id}
                key={child.id}
                treeData={treeData}
                setProps={setProps}
                selectedId={selectedId}
              />
            ))}
        </div>
      )}
    </div>
  )
}

export default TreeNode
